import { FC, useState } from 'react'
import { PerInfoBox } from '../../../components/custom_components/InfoBox'
import { PerInfoBoxNumberElement } from '../../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { InfoBoxTile } from '../../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { Col, Spin, Divider, Select } from 'antd'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { useTranslation } from 'react-i18next'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { CompaniesForRankingReportDto, RankingReportFilterInput } from '../../../types'
import { PerModal } from '../../../components/custom_components/Modal'
import { createZipWithSelectedRankingReports } from '../../../graphql/reports/mutationsReport'
import { showNotification } from '../../../components/custom_components/Notification/notification'
const { Option } = Select

type RankingReportsInfoBoxProps = {
  isLoading: boolean
  counter?: number
  reportsSelectedRows: string[]
  setReportsSelectedRows: (value: string[]) => void
  rankingReportsFilters: RankingReportFilterInput
  setRankingReportsFilters: (value: RankingReportFilterInput) => void
  possibleYears: number[]
  possibleCompanies: CompaniesForRankingReportDto[]
}

export const RankingReportsInfoBox: FC<RankingReportsInfoBoxProps> = ({
  isLoading,
  counter,
  reportsSelectedRows,
  setReportsSelectedRows,
  rankingReportsFilters,
  setRankingReportsFilters,
  possibleCompanies,
  possibleYears,
}) => {
  const { t } = useTranslation()
  const [downloadModalVisible, setDownloadModalVisible] = useState<boolean>(false)

  return (
    <>
      <PerInfoBox>
        <PerFlexRow justify="space-between" width="fullWidth" className="fullHeight">
          <Col span={5} className="fullHeight">
            <InfoBoxTile
              heading={t('reports.rankingInfoBox.download')}
              contentFlex="fixed"
              headingAlign="center"
              contentAlign="space-between"
            >
              <PerFlexRow direction="column" align="middle" justify="space-between" className="fullHeight">
                <PerFlexRow justify="center">
                  <PerText fontSize="xs" color="secondary" appearance="quote" align="center">
                    {t('reports.rankingInfoBox.downloadDescription')}
                  </PerText>
                </PerFlexRow>
                <PerButtonCircle
                  tooltip={reportsSelectedRows.length < 2 ? t('reports.rankingInfoBox.selectOneOrMoreReports') : undefined}
                  color="primary"
                  variant="pastel"
                  icon="download_fill"
                  onClick={async () => {
                    setDownloadModalVisible(true)
                  }}
                  size="large"
                  disabled={reportsSelectedRows.length < 2}
                />
              </PerFlexRow>
            </InfoBoxTile>
          </Col>

          <Divider type="vertical" className="fullHeight" />

          <Col span={14} className="fullHeight">
            <InfoBoxTile heading={t('reports.rankingInfoBox.filters')} contentFlex="fixed" headingAlign="left" contentAlign="start">
              <Col span={10} className="fullHeight">
                <PerFlexRow direction="column" justify="space-between" width="fullWidth" className="fullHeight">
                  <PerFlexRow>
                    <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                      {t('reports.rankingInfoBox.company')}
                    </PerText>
                    <Select
                      allowClear
                      onChange={(id: string) => {
                        setRankingReportsFilters({ ...rankingReportsFilters, company: possibleCompanies.find((c) => c.companyId === id) })
                      }}
                      value={rankingReportsFilters.company ? rankingReportsFilters.company.companyId : undefined}
                      placeholder={t('reports.rankingInfoBox.selectCompany')}
                      style={{ width: '100%' }}
                    >
                      {[...possibleCompanies]
                        .sort((companyA, companyB) => {
                          return companyA.companyName.localeCompare(companyB.companyName)
                        })
                        .map((company) => (
                          <Option key={company.companyId} value={company.companyId}>
                            {company.companyName}
                          </Option>
                        ))}
                    </Select>
                  </PerFlexRow>
                  <PerFlexRow>
                    <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                      {t('reports.rankingInfoBox.year')}
                    </PerText>

                    <Select
                      allowClear
                      onChange={(year: number) => {
                        setRankingReportsFilters({ ...rankingReportsFilters, year: year })
                      }}
                      value={rankingReportsFilters.year ? rankingReportsFilters.year : undefined}
                      placeholder={t('reports.rankingInfoBox.selectYear')}
                      style={{ width: '100%' }}
                    >
                      {[...possibleYears]
                        .sort((yearA, yearB) => {
                          return yearB - yearA
                        })
                        .map((year) => (
                          <Option key={year} value={year}>
                            {year}
                          </Option>
                        ))}
                    </Select>
                  </PerFlexRow>
                </PerFlexRow>
              </Col>
            </InfoBoxTile>
          </Col>

          <Divider type="vertical" className="fullHeight" />

          <Col span={4} className="fullHeight">
            <InfoBoxTile heading={t('reports.rankingInfoBox.reportsCounter')} contentFlex="fixed" headingAlign="left">
              <PerFlexRow
                width="fullWidth"
                direction="column"
                align={isLoading ? 'middle' : 'top'}
                justify={isLoading ? 'center' : 'end'}
                className="fullHeight"
              >
                {isLoading ? (
                  <Spin size="large" spinning />
                ) : (
                  <PerInfoBoxNumberElement number={`${counter !== undefined ? counter : '?'}`} />
                )}
              </PerFlexRow>
            </InfoBoxTile>
          </Col>
        </PerFlexRow>
      </PerInfoBox>
      {downloadModalVisible && (
        <DownloadsZipModal
          isModalOpen={downloadModalVisible}
          reportsSelectedRows={reportsSelectedRows}
          closeModal={() => {
            setDownloadModalVisible(false)
            setReportsSelectedRows([])
          }}
        />
      )}
    </>
  )
}

export const DownloadsZipModal: FC<{ isModalOpen: boolean; reportsSelectedRows: string[]; closeModal: () => void }> = ({
  isModalOpen,
  closeModal,
  reportsSelectedRows,
}) => {
  const [t] = useTranslation()
  const [downloadProcessing, toggleDownloadProcessing] = useState<boolean>(false)
  const [reportsDownloadErrorVisible, setReportsDownloadErrorVisible] = useState<boolean>(false)

  return (
    <PerModal
      centered
      visible={isModalOpen}
      onCancel={closeModal}
      title={{
        heading: t('reports.rankingInfoBox.downloadConfirmHeading'),
      }}
      footer={{
        confirmBtn: {
          onClick: async () => {
            toggleDownloadProcessing(true)
            const { data, errors } = await createZipWithSelectedRankingReports(reportsSelectedRows)
            if (!!data && !errors) {
              closeModal()
              showNotification({
                type: 'success',
                message: t('reports.rankingInfoBox.notifications.success.title'),
                description: t('reports.rankingInfoBox.notifications.success.description'),
              })
            } else {
              setReportsDownloadErrorVisible(true)
            }
            toggleDownloadProcessing(false)
          },
          disabled: downloadProcessing || reportsDownloadErrorVisible,
          isProcessing: { active: downloadProcessing },
        },
        cancelBtn: { onClick: closeModal },
      }}
    >
      <PerText>{t('reports.rankingInfoBox.downloadConfirmMessage')}</PerText>

      {reportsDownloadErrorVisible && (
        <PerText color="error" mt="xs">
          {t('reports.rankingInfoBox.downloadErrorMessage')}
        </PerText>
      )}
    </PerModal>
  )
}
